import { createFavorite, deleteFavorite } from 'flow/actions/favorites-actions';
import CallTransferDialogWrapper from 'flow/components/call-transfer-dialog/CallTransferDialogWrapper';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { isExtensionStatusInCall } from 'flow/utils';
import {
    getFixedNumberFromContact,
    getMobileNumberFromContact,
    getTechnicalNumberFromContact,
} from 'flow/utils/extension-utils';
import { FavoriteType } from 'flow/utils/favorite-utils';
import { fromJS } from 'immutable';
import { memo, MouseEventHandler, useState } from 'react';
import CallButtonMenuItem from './CallButtonMenuItem';
import QuickTransferMenuItem from './QuickTransferMenuItem';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectFavoriteFromEntityKey } from 'flow/selectors/favorite-selectors';

interface Props {
    extensionKey: ExtensionKey;
}

const AiContactContextMenu = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { extension } = useExtension(extensionKey);
    const contact = extension?.get('contact');
    const favorite = useAppSelector((state) =>
        selectFavoriteFromEntityKey(extensionKey, state.favorites),
    );

    const thisExtensionIsUnavailable = useAppSelector((state) =>
        isExtensionStatusInCall(
            state.extensions.getIn(['statuses', extensionKey]),
        ),
    );

    const [transferSelectionDialogOpen, setTransferSelectionDialogOpen] =
        useState(false);

    const toggleFavorite: MouseEventHandler = (event) => {
        if (!favorite) {
            const favoriteDTO = fromJS({
                type: FavoriteType.extension,
                extension: extension,
            });
            dispatch(createFavorite(favoriteDTO));
        } else {
            dispatch(deleteFavorite(favorite.get('key')));
        }

        event.stopPropagation();
    };

    const mobileNumber = getMobileNumberFromContact(contact);
    const fixedNumber = getFixedNumberFromContact(contact);
    const technicalNumber = getTechnicalNumberFromContact(contact);

    return (
        <>
            {!transferSelectionDialogOpen && (
                <>
                    <CallButtonMenuItem
                        fixedNumber={fixedNumber}
                        technicalNumber={technicalNumber}
                        mobileNumber={mobileNumber}
                        extensionToCall={extension}
                    />

                    <QuickTransferMenuItem
                        openTransferSelectionDialog={() =>
                            setTransferSelectionDialogOpen(true)
                        }
                        fixedNumber={fixedNumber}
                        mobileNumber={mobileNumber}
                        transferContact={contact}
                        handleClose={() => {}}
                    />

                    <MenuItem onClick={toggleFavorite}>
                        {favorite
                            ? i18next.t('General.Favorite.Remove.Favorite')
                            : i18next.t('General.Favorite.Add.Favorite')}
                    </MenuItem>
                </>
            )}
            {transferSelectionDialogOpen && (
                <CallTransferDialogWrapper
                    phoneNumber={fixedNumber}
                    contact={contact}
                    transferContact={contact}
                    handleClose={() => setTransferSelectionDialogOpen(false)}
                    thisExtensionIsUnavailable={thisExtensionIsUnavailable}
                    logLocation={'quick transfer context menu'}
                />
            )}
        </>
    );
};

export default memo(AiContactContextMenu);
